// Auth v1 (JWT) for the Studio. The Studio has no login of its own: the store is the only place to
// sign in, and the Studio SHARES the store's session — same origin, so the same JWT pair
// (localStorage `wgt_auth_v1`) and the same HttpOnly refresh cookie. There is no Studio pair any more
// (it used to get one through a handoff code): with the refresh token in a host cookie, a second pair
// would overwrite the store's cookie and both apps would fight over one refresh token.
// - `/my?t=<code>` (old store links) is still accepted: the code is exchanged into the shared pair.
// - Refresh is serialized across ALL tabs of both apps with `navigator.locks` ('wgt-refresh', the
//   store uses the same name), so two tabs never rotate the same refresh token concurrently.
// Anything else ends in a 401, which sends the user to the store's login page with `next`.
//
// Uses raw fetch (not the shared Api) on purpose: the v1 auth endpoints are stateless/CSRF-exempt,
// and routing through Api would create an import cycle (Api -> getAuthorizationHeaders -> authV1).

import AuthStorage from './authStorage';

const V1_BASE = '/api/v1/auth';

export function isHijackSession() {
  return AuthStorage.isHijack();
}

export function clearHijackSession() {
  AuthStorage.clearHijack();
}

async function postV1(path, body, headers = {}) {
  const res = await fetch(`${V1_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      // Web client: the backend keeps the refresh token in the HttpOnly cookie, not in the JSON.
      'X-Client': 'web',
      ...headers,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const error = new Error(`v1 ${path} -> ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return res.status === 204 ? null : res.json();
}

function generateUuid() {
  if (
    typeof window !== 'undefined' &&
    window.crypto &&
    typeof window.crypto.randomUUID === 'function'
  ) {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    // eslint-disable-next-line no-bitwise
    const rand = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

// Stable per-browser id that binds the refresh token (anti-fraud / limits). Same key the store uses.
export function getDeviceId() {
  try {
    let id = AuthStorage.getDeviceId();
    if (!id) {
      id = generateUuid();
      AuthStorage.setDeviceId(id);
    }
    return id;
  } catch (e) {
    return undefined;
  }
}

export function getV1Tokens() {
  return AuthStorage.getTokens();
}

export function getV1Access() {
  const tokens = getV1Tokens();
  return (tokens && tokens.accessToken) || null;
}

// Accepts the raw (snake_case) token-pair response.
export function setV1Tokens(pair) {
  AuthStorage.setTokens(pair);
}

export function clearV1Tokens() {
  AuthStorage.clearTokens();
}

function isAccessExpired(tokens) {
  if (!tokens || !tokens.obtainedAt || !tokens.expiresIn) return false;
  return Date.now() >= tokens.obtainedAt + tokens.expiresIn * 1000;
}

// Serialize across tabs of both apps; without Web Locks (old browsers) run unguarded.
function withRefreshLock(fn) {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('wgt-refresh', fn);
  }
  return fn();
}

// Trade a one-time handoff code (old `/my?t=` store links) for the shared JWT pair.
export async function exchangeHandoff(code) {
  const res = await postV1('/token/exchange/', {
    code,
    device_id: getDeviceId(),
  });
  setV1Tokens(res);
  return res;
}

let inFlightRefresh = null;

async function doRefresh(staleAccess) {
  // Another tab (store or Studio) may have rotated the pair while we waited for the lock.
  const current = getV1Access();
  if (current && current !== staleAccess) return true;
  const tokens = getV1Tokens();
  const body = {
    device_id: getDeviceId(),
    ...(tokens && tokens.refreshToken
      ? { refresh_token: tokens.refreshToken }
      : {}),
  };
  try {
    const res = await postV1('/token/refresh/', body);
    if (res && res.access_token) {
      setV1Tokens(res);
      return true;
    }
    return false;
  } catch (e) {
    // Only a rejected refresh ends the session; a network error keeps the pair for the next try.
    if (e && e.status === 401) clearV1Tokens();
    return false;
  }
}

// Refresh the access token. Returns true on success. Single-flight within the tab, locked across tabs.
export function refreshV1() {
  if (!inFlightRefresh) {
    const staleAccess = getV1Access();
    inFlightRefresh = withRefreshLock(() => doRefresh(staleAccess)).finally(
      () => {
        inFlightRefresh = null;
      }
    );
  }
  return inFlightRefresh;
}

// Run before the app renders so the auth gate already sees a logged-in user. Best-effort: on
// failure the profile request answers 401 and the user is sent to the store's login page.
export async function ensureV1Session() {
  try {
    AuthStorage.clearLegacyStudioTokens();
    const params = new URLSearchParams(window.location.search);
    const code = params.get('t') || params.get('handoff_code');
    const hijack = params.get('hijack') === '1' && !code;
    if (code || hijack) {
      params.delete('t');
      params.delete('handoff_code');
      params.delete('hijack');
      const qs = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
      );
    }
    if (hijack) {
      AuthStorage.setHijack(true);
      return;
    }
    if (code) {
      clearHijackSession();
      // Already signed in through the store → the shared pair is enough; don't mint another one.
      if (!getV1Access()) await exchangeHandoff(code);
      return;
    }
    if (isHijackSession()) return;

    const tokens = getV1Tokens();
    if (tokens && !isAccessExpired(tokens)) return;
    // Expired access, or a cookie-only session: refresh through the HttpOnly cookie.
    await refreshV1();
  } catch (e) {
    // ignore — a 401 on the profile request takes the user to the store's login
  }
}

// Sign out of both the Studio and the store: it is one shared session.
export async function logoutV1() {
  const tokens = getV1Tokens();
  clearV1Tokens();
  await postV1('/token/logout/', {
    device_id: getDeviceId(),
    ...(tokens && tokens.refreshToken
      ? { refresh_token: tokens.refreshToken }
      : {}),
  }).catch(() => null);
}

// The store's login page; it sends the user back to `next` once signed in.
export function storeLoginUrl(next) {
  return `${window.location.origin}/login/?next=${encodeURIComponent(
    next || window.location.pathname + window.location.search
  )}`;
}

export function redirectToStoreLogin(next) {
  window.location.href = storeLoginUrl(next);
}
